import React, { useState, useEffect, useRef, useContext } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  IconButton,
  Tooltip,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
} from "@mui/material";
import {
  NavigateBefore,
  NavigateNext,
  Today,
  MyLocation,
  Refresh,
  Info,
  Close,
  CalendarToday,
} from "@mui/icons-material";
import { DateTime } from "luxon";
import mapService from "services/map.service";
import { Link } from "react-router-dom";
import RabbitContext from "context/rabbit.js";
import utils from "utils/utils.js";
// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
// Material Dashboard 2 PRO React example components
import DashboardLayout from "components/LayoutContainers/DashboardLayout";
import DashboardNavbar from "components/Navbars/DashboardNavbar";
import Footer from "components/Footer";
import Card from "@mui/material/Card";

// Fix for default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

// Custom icons
const orderIcon = L.icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const selectedOrderIcon = L.icon({
  iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const userLocationIcon = L.icon({
  iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});


const MapPage = () => {
  const { rabbit } = useContext(RabbitContext);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userLocation, setUserLocation] = useState(null);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [orderDetails, setOrderDetails] = useState(null);
  const [distanceDialog, setDistanceDialog] = useState(null);
  const [error, setError] = useState(null);
  const [orderStats, setOrderStats] = useState({
    total: 0,
    withCoords: 0,
    withoutCoords: 0,
    geocoded: 0,
    failed: 0
  });
  const mapRef = useRef();
  const mapContainerRef = useRef();
  const markersRef = useRef(new Map());
  const polylineRef = useRef(null);

  // Load orders for current date
  const loadOrders = async (date) => {
    setLoading(true);
    setError(null);
    
    // Initialize stats
    let stats = {
      total: 0,
      withCoords: 0,
      withoutCoords: 0,
      geocoded: 0,
      failed: 0
    };
    
    try {
      const response = await mapService.getOrdersForMap(
        date,
        rabbit.companyId
      );
      
      // Convert target date to format for comparison
      const targetDateStr = DateTime.fromJSDate(date).toFormat('yyyy-MM-dd');
      
      // Filter orders by calculated delivery date
      const dateFilteredOrders = response.data.filter(order => {
        // Calculate delivery date: dateBegin + (quantity - 1) days
        const deliveryDate = DateTime.fromISO(order.dateBegin).plus({ days: order.quantity - 1 });
        const deliveryDateStr = deliveryDate.toFormat('yyyy-MM-dd');
        
        return deliveryDateStr === targetDateStr;
      });
      
      stats.total = dateFilteredOrders.length;
      
      // Separate orders with and without coordinates
      const ordersWithCoords = dateFilteredOrders.filter(order => order.latitude && order.longitude);
      const ordersWithoutCoords = dateFilteredOrders.filter(order => !order.latitude || !order.longitude);
      
      stats.withCoords = ordersWithCoords.length;
      stats.withoutCoords = ordersWithoutCoords.length;
      
      console.log('Total delivered orders:', response.data.length);
      console.log('Orders for date', targetDateStr, ':', dateFilteredOrders.length);
      console.log('Orders with coordinates:', ordersWithCoords.length);
      console.log('Orders without coordinates:', ordersWithoutCoords.length);
      
      // Show orders with coordinates immediately
      setOrders(ordersWithCoords);
      setOrderStats(stats);
      
      // Geocode orders without coordinates
      if (ordersWithoutCoords.length > 0) {
        console.log('Geocoding orders without coordinates...');
        
        // Geocode each order without coordinates (with delay to avoid rate limiting)
        for (const order of ordersWithoutCoords) {
          try {
            const response = await mapService.geocodeOrder(order.id);
            
            if (response.data.success) {
              console.log(`Successfully geocoded order ${order.id}`);
              stats.geocoded++;
              
              // Refresh the orders list to get the updated coordinates
              const refreshResponse = await mapService.getOrdersForMap(date, rabbit.companyId);
              const refreshedOrders = refreshResponse.data.filter(o => {
                const deliveryDate = DateTime.fromISO(o.dateBegin).plus({ days: o.quantity - 1 });
                return deliveryDate.toFormat('yyyy-MM-dd') === targetDateStr && o.latitude && o.longitude;
              });
              setOrders(refreshedOrders);
              stats.withCoords = refreshedOrders.length;
            } else {
              console.log(`Geocoding failed for order ${order.id}: ${response.data.message}`);
              stats.failed++;
            }
          } catch (error) {
            console.error(`Failed to geocode order ${order.id}:`, error);
            stats.failed++;
          }
          
          // Update stats after each attempt
          setOrderStats({...stats});
          
          // Add delay between requests to avoid rate limiting
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
      
      // Update map bounds to show all orders
      if (ordersWithCoords.length > 0) {
        const bounds = L.latLngBounds(ordersWithCoords.map((order) => [order.latitude, order.longitude]));
        if (mapRef.current) {
          mapRef.current.fitBounds(bounds, { padding: [50, 50] });
        }
      }
    } catch (err) {
      console.error("Error loading orders:", err);
      setError("Errore nel caricamento degli ordini");
    } finally {
      setLoading(false);
    }
  };

  // Get user location
  const getUserLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lon: position.coords.longitude,
          });
        },
        (error) => {
          console.error("Error getting location:", error);
          setError("Impossibile ottenere la posizione corrente");
        }
      );
    } else {
      setError("Geolocalizzazione non supportata dal browser");
    }
  };

  // Handle marker click
  const handleMarkerClick = async (order) => {
    try {
      const response = await mapService.getOrderDetails(
        order.id,
        userLocation?.lat,
        userLocation?.lon
      );
      setOrderDetails(response.data);
      
      // Update the selected order with full product details
      setSelectedOrders(prev => prev.map(o => 
        o.id === order.id ? { ...o, product: response.data.product } : o
      ));
    } catch (err) {
      console.error("Error getting order details:", err);
      setError("Errore nel caricamento dei dettagli dell'ordine");
    }
  };

  // Handle map click (for deselecting markers)
  const handleMapClick = () => {
    if (selectedOrders.length === 1) {
      setSelectedOrders([]);
    }
  };

  // Handle marker selection for distance calculation
  const handleMarkerSelect = (order) => {
    if (selectedOrders.length === 0) {
      setSelectedOrders([order]);
    } else if (selectedOrders.length === 1) {
      if (selectedOrders[0].id === order.id) {
        setSelectedOrders([]);
      } else {
        const newSelection = [...selectedOrders, order];
        setSelectedOrders(newSelection);
        calculateDistance(newSelection[0], newSelection[1]);
      }
    } else {
      setSelectedOrders([order]);
    }
  };

  // Calculate distance between two orders
  const calculateDistance = async (order1, order2) => {
    try {
      // Get both air and route distances
      const [airResponse, routeResponse] = await Promise.all([
        mapService.getDistanceBetweenOrders(order1.id, order2.id, "air"),
        mapService.getDistanceBetweenOrders(order1.id, order2.id, "route")
      ]);
      
      setDistanceDialog({
        order1: {
          number: `${order1.number}-${order1.code}`,
          address: `${order1.customer?.address}, ${order1.customer?.city}`
        },
        order2: {
          number: `${order2.number}-${order2.code}`,
          address: `${order2.customer?.address}, ${order2.customer?.city}`
        },
        airDistance: airResponse.data.distance,
        routeDistance: routeResponse.data.distance,
        routeDuration: routeResponse.data.duration
      });
    } catch (err) {
      console.error("Error calculating distance:", err);
      setError("Errore nel calcolo della distanza");
    }
  };

  // Navigate dates
  const navigateDate = (direction) => {
    const newDate = new Date(currentDate);
    if (direction === "prev") {
      newDate.setDate(newDate.getDate() - 1);
    } else if (direction === "next") {
      newDate.setDate(newDate.getDate() + 1);
    } else if (direction === "today") {
      newDate.setTime(new Date().getTime());
    }
    setCurrentDate(newDate);
  };

  // Reset selections
  const resetSelections = () => {
    setSelectedOrders([]);
    setOrderDetails(null);
    setDistanceDialog(null);
  };

  // Initialize map
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    // Create map
    const map = L.map(mapContainerRef.current).setView([41.9028, 12.4964], 13);
    mapRef.current = map;

    // Add tile layer
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
    }).addTo(map);

    // Handle map click
    map.on('click', handleMapClick);

    return () => {
      // Cleanup
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  // Update markers when orders change
  useEffect(() => {
    if (!mapRef.current) return;

    // Clear existing markers
    markersRef.current.forEach(marker => {
      mapRef.current.removeLayer(marker);
    });
    markersRef.current.clear();

    // Add order markers
    orders.forEach(order => {
      const isSelected = selectedOrders.some(o => o.id === order.id);
      const marker = L.marker([order.latitude, order.longitude], {
        icon: isSelected ? selectedOrderIcon : orderIcon
      });

      // Create popup content
      const popupContent = `
        <div>
          <strong>Ordine ${order.number}-${order.code}</strong><br>
          ${order.customer?.surname} ${order.customer?.name}<br>
          ${order.customer?.address}, ${order.customer?.city}
          ${order.product ? `<br>Prodotto: ${order.product}` : ''}
        </div>
      `;

      marker.bindPopup(popupContent);
      marker.on('click', () => {
        handleMarkerClick(order);
        handleMarkerSelect(order);
      });

      marker.addTo(mapRef.current);
      markersRef.current.set(order.id, marker);
    });

    // Add user location marker
    if (userLocation) {
      const userMarker = L.marker([userLocation.lat, userLocation.lon], {
        icon: userLocationIcon
      });
      userMarker.bindPopup('La tua posizione');
      userMarker.addTo(mapRef.current);
      markersRef.current.set('user', userMarker);
    }

    // Fit bounds if there are orders
    if (orders.length > 0) {
      const bounds = L.latLngBounds(orders.map(o => [o.latitude, o.longitude]));
      mapRef.current.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [orders, selectedOrders, userLocation]);

  // Update polyline when two orders are selected
  useEffect(() => {
    if (!mapRef.current) return;

    // Remove existing polyline
    if (polylineRef.current) {
      mapRef.current.removeLayer(polylineRef.current);
      polylineRef.current = null;
    }

    // Add new polyline if two orders are selected
    if (selectedOrders.length === 2) {
      const polyline = L.polyline([
        [selectedOrders[0].latitude, selectedOrders[0].longitude],
        [selectedOrders[1].latitude, selectedOrders[1].longitude]
      ], {
        color: 'red',
        weight: 3
      });
      polyline.addTo(mapRef.current);
      polylineRef.current = polyline;
    }
  }, [selectedOrders]);

  // Effects
  useEffect(() => {
    loadOrders(currentDate);
  }, [currentDate]);

  useEffect(() => {
    getUserLocation();
  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        {/* Header with controls */}
        <MDBox mb={3}>
          <Card>
            <MDBox p={3}>
              <MDBox display="flex" alignItems="center" justifyContent="space-between">
                <MDBox display="flex" alignItems="center" gap={2}>
                  <IconButton onClick={() => navigateDate("prev")}>
                    <NavigateBefore />
                  </IconButton>
                  <MDInput
                    type="date"
                    label="Data"
                    value={DateTime.fromJSDate(currentDate).toFormat("yyyy-MM-dd")}
                    onChange={(e) => {
                      if (e.target.value) {
                        setCurrentDate(new Date(e.target.value));
                      }
                    }}
                    sx={{ minWidth: 150 }}
                  />
                  <IconButton onClick={() => navigateDate("next")}>
                    <NavigateNext />
                  </IconButton>
                  <MDButton
                    variant="gradient"
                    color="info"
                    startIcon={<Today />}
                    onClick={() => navigateDate("today")}
                  >
                    Oggi
                  </MDButton>
                </MDBox>

                <MDBox display="flex" alignItems="center" gap={2}>
                  <Chip
                    label={`${orders.length} ordini`}
                    color="primary"
                    variant="outlined"
                  />
                  <Tooltip title="Centra sulla mia posizione">
                    <IconButton onClick={getUserLocation}>
                      <MyLocation />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Ricarica">
                    <IconButton onClick={() => loadOrders(currentDate)}>
                      <Refresh />
                    </IconButton>
                  </Tooltip>
                  {(selectedOrders.length > 0 || orderDetails || distanceDialog) && (
                    <MDButton variant="gradient" color="error" onClick={resetSelections}>
                      Reset
                    </MDButton>
                  )}
                </MDBox>
              </MDBox>
            </MDBox>
          </Card>
        </MDBox>

        {/* Map and Details Container */}
        <MDBox display="flex" gap={2}>
          {/* Map - 9/10 columns */}
          <MDBox flex={9}>
            {/* Statistics Display */}
            <MDBox mb={1}>
              <MDBox display="flex" gap={2} flexWrap="wrap">
                <Chip 
                  label={`Totali: ${orderStats.total}`}
                  color="info"
                  size="small"
                />
                <Chip 
                  label={`Con coordinate: ${orderStats.withCoords}`}
                  color="success"
                  size="small"
                />
                <Chip 
                  label={`Senza coordinate: ${orderStats.withoutCoords}`}
                  color="warning"
                  size="small"
                />
                {orderStats.geocoded > 0 && (
                  <Chip 
                    label={`Geocodificati: ${orderStats.geocoded}`}
                    color="primary"
                    size="small"
                  />
                )}
                {orderStats.failed > 0 && (
                  <Chip 
                    label={`Falliti: ${orderStats.failed}`}
                    color="error"
                    size="small"
                  />
                )}
              </MDBox>
            </MDBox>
            
            <Card sx={{ height: "600px", position: "relative" }}>
              {loading && (
                <MDBox
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "rgba(255, 255, 255, 0.8)",
                    zIndex: 1000,
                  }}
                >
                  <CircularProgress />
                </MDBox>
              )}

              <div
                ref={mapContainerRef}
                style={{ height: "100%", width: "100%" }}
              />
            </Card>
          </MDBox>

          {/* Details Panel - 3/10 columns */}
          <MDBox flex={3}>
            <Card sx={{ height: "600px", overflow: "auto" }}>
              <MDBox p={2}>
                {selectedOrders.length === 0 ? (
                  <MDTypography variant="body2" color="textSecondary">
                    Seleziona uno o più ordini sulla mappa per vedere i dettagli
                  </MDTypography>
                ) : (
                  <MDBox display="flex" flexDirection="column" gap={3}>
                    {/* Selected Order 1 */}
                    {selectedOrders[0] && (
                      <MDBox>
                        <MDTypography variant="h6" gutterBottom>
                          Punto 1
                        </MDTypography>
                        <MDBox display="flex" flexDirection="column" gap={1}>
                          <MDTypography variant="subtitle2">
                            Ordine{" "}
                            <Link to={`/customers/${selectedOrders[0].customerId}/orders/${selectedOrders[0].id}`} style={{ textDecoration: "none" }}>
                              <MDTypography component="span" variant="subtitle2" color="info">
                                {selectedOrders[0].number}-{selectedOrders[0].code}
                              </MDTypography>
                            </Link>
                          </MDTypography>
                          <MDTypography variant="body2">
                            {selectedOrders[0].customer?.surname} {selectedOrders[0].customer?.name}, {selectedOrders[0].customer?.address}, {selectedOrders[0].customer?.city}
                          </MDTypography>
                          <MDTypography variant="body2" color="textSecondary">
                            Tel: {selectedOrders[0].customer?.phone || "-"}
                          </MDTypography>
                          <MDTypography variant="body2" color="textSecondary">
                            Data scadenza: {utils.formatDate(
                              DateTime.fromISO(selectedOrders[0].dateBegin).plus({ days: selectedOrders[0].quantity - 1 }),
                              "dd/MM/yyyy"
                            )}
                          </MDTypography>
                          
                          {/* Device Details */}
                          {selectedOrders[0].product && (
                            <MDBox mt={1}>
                              <MDTypography variant="body2" fontWeight="medium">
                                Dispositivo da ritirare:
                              </MDTypography>
                              <MDTypography variant="body2" color="textSecondary">
                                {selectedOrders[0].product.productModel?.category || ''} {selectedOrders[0].product.model || ''}
                                {selectedOrders[0].product.serial ? ` ser. ${selectedOrders[0].product.serial}` : ''}
                                {selectedOrders[0].product.batch ? ` cod.f. ${selectedOrders[0].product.batch}` : ''}
                                {selectedOrders[0].code ? ` [${selectedOrders[0].code}]` : ''}
                              </MDTypography>
                            </MDBox>
                          )}

                          {/* Distance from user */}
                          {userLocation && orderDetails?.distances && orderDetails.id === selectedOrders[0].id && (
                            <MDBox mt={1}>
                              <MDTypography variant="body2" fontWeight="medium">
                                Distanza dalla tua posizione:
                              </MDTypography>
                              <MDTypography variant="body2" color="textSecondary">
                                In linea d'aria: {orderDetails.distances.air.toFixed(2)} km
                              </MDTypography>
                              {orderDetails.distances.route && (
                                <>
                                  <MDTypography variant="body2" color="textSecondary">
                                    Percorso stradale: {orderDetails.distances.route.toFixed(2)} km
                                  </MDTypography>
                                  {orderDetails.distances.duration && (
                                    <MDTypography variant="body2" color="textSecondary">
                                      Durata: {Math.round(orderDetails.distances.duration)} min
                                    </MDTypography>
                                  )}
                                </>
                              )}
                            </MDBox>
                          )}
                        </MDBox>
                      </MDBox>
                    )}

                    {/* Selected Order 2 */}
                    {selectedOrders[1] && (
                      <MDBox>
                        <MDTypography variant="h6" gutterBottom>
                          Punto 2
                        </MDTypography>
                        <MDBox display="flex" flexDirection="column" gap={1}>
                          <MDTypography variant="subtitle2">
                            Ordine{" "}
                            <Link to={`/customers/${selectedOrders[1].customerId}/orders/${selectedOrders[1].id}`} style={{ textDecoration: "none" }}>
                              <MDTypography component="span" variant="subtitle2" color="info">
                                {selectedOrders[1].number}-{selectedOrders[1].code}
                              </MDTypography>
                            </Link>
                          </MDTypography>
                          <MDTypography variant="body2">
                            {selectedOrders[1].customer?.surname} {selectedOrders[1].customer?.name}, {selectedOrders[1].customer?.address}, {selectedOrders[1].customer?.city}
                          </MDTypography>
                          <MDTypography variant="body2" color="textSecondary">
                            Tel: {selectedOrders[1].customer?.phone || "-"}
                          </MDTypography>
                          <MDTypography variant="body2" color="textSecondary">
                            Data scadenza: {utils.formatDate(
                              DateTime.fromISO(selectedOrders[1].dateBegin).plus({ days: selectedOrders[1].quantity - 1 }),
                              "dd/MM/yyyy"
                            )}
                          </MDTypography>
                          
                          {/* Device Details */}
                          {selectedOrders[1].product && (
                            <MDBox mt={1}>
                              <MDTypography variant="body2" fontWeight="medium">
                                Dispositivo da ritirare:
                              </MDTypography>
                              <MDTypography variant="body2" color="textSecondary">
                                {selectedOrders[1].product.productModel?.category || ''} {selectedOrders[1].product.model || ''}
                                {selectedOrders[1].product.serial ? ` ser. ${selectedOrders[1].product.serial}` : ''}
                                {selectedOrders[1].product.batch ? ` cod.f. ${selectedOrders[1].product.batch}` : ''}
                                {selectedOrders[1].code ? ` [${selectedOrders[1].code}]` : ''}
                              </MDTypography>
                            </MDBox>
                          )}
                        </MDBox>
                      </MDBox>
                    )}

                    {/* Distance between orders */}
                    {selectedOrders.length === 2 && distanceDialog && (
                      <MDBox>
                        <MDTypography variant="h6" gutterBottom>
                          Distanza Punti
                        </MDTypography>
                        <MDBox display="flex" flexDirection="column" gap={1}>
                          <MDTypography variant="body2">
                            <strong>In linea d'aria:</strong> {distanceDialog.airDistance?.toFixed(2) || "N/A"} km
                          </MDTypography>
                          <MDTypography variant="body2">
                            <strong>Percorso stradale:</strong> {distanceDialog.routeDistance?.toFixed(2) || "N/A"} km
                          </MDTypography>
                          {distanceDialog.routeDuration && (
                            <MDTypography variant="body2" color="textSecondary">
                              Durata: {Math.round(distanceDialog.routeDuration)} min
                            </MDTypography>
                          )}
                        </MDBox>
                      </MDBox>
                    )}
                  </MDBox>
                )}
              </MDBox>
            </Card>
          </MDBox>
        </MDBox>
      </MDBox>
      <Footer />

      {/* Error snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
};

export default MapPage;
